import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateProductsStationChain,
  useUpdateProductsStationChain,
} from '@/api/generated/products-station-chains/products-station-chains';
import {
  buildStationChainFormSchema,
  emptyStationChainFormValues,
  type StationChainFormValues,
} from './stationChainFormSchema';
import { toStationChainRequestBody } from './toStationChainRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  Color: 'color',
};

interface UseStationChainFormOptions {
  /** Bez `stationChainId` = zakládání, s `stationChainId` = editace. */
  stationChainId?: string;
  initialValues?: StationChainFormValues;
  /** Voláno po úspěšném uložení (předává id uložené trasy) — invalidace, toast; NEzavírá okno. */
  onSaved: (id: string) => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulářová logika trasy (sdílená pro zakládání i editaci) — TanStack Form se Zod validací,
 * create/update mutace a promítnutí serverových validačních chyb do polí. `submit()` vrací, zda se
 * uložení povedlo (pro „Uložit & zpět" ve `FormActionBar`).
 */
export function useStationChainForm({
  stationChainId,
  initialValues,
  onSaved,
  onError,
}: UseStationChainFormOptions) {
  const { t } = useTranslation('products');
  const schema = useMemo(() => buildStationChainFormSchema(t), [t]);

  const createMutation = useCreateProductsStationChain();
  const updateMutation = useUpdateProductsStationChain();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyStationChainFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toStationChainRequestBody(value);
      try {
        if (stationChainId != null) {
          await updateMutation.mutateAsync({ id: stationChainId, data });
          onSaved(stationChainId);
        } else {
          const newId = await createMutation.mutateAsync({ data });
          onSaved(newId);
        }
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu. */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return {
    form,
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}

/** Typ instance TanStack Form pro detail trasy (pro propsy podřízených komponent). */
export type StationChainFormApi = ReturnType<typeof useStationChainForm>['form'];
